import { DrawerBase } from '../../core/abstraction/DrawerBase';
import { bonusPreBonusSharedResources, bonusPreBonusSharedRssNames } from '../../core/gameConstants';
import { FONT_BALOO_NAME, FONT_LATO_NAME } from '../../core/constats';
import { getTranslation, translationKeys } from '../../core/translation/translation';
import { animationDrawer } from '../../core/AnimationDrawer';

class BonusPreBonusSharedDrawer extends DrawerBase {
  constructor() {
    super();
    this.loader = this.createLoader();
    this._resources = null;
    this.nativeConfig = {
      titleStyle: {
        fill: [0xfefefd, 0x9b7853],
        fontFamily: FONT_LATO_NAME,
        stroke: '#dec195',
        strokeThickness: 1,
        fontSize: 24,
        align: 'center',
        fontWeight: 900,
      },
      multiplierStyle: {
        fill: [0xffffff, 0xB37AF5],
        fontFamily: FONT_BALOO_NAME,
        fontWeight: 900,
        stroke: '#FFFFFF',
        strokeThickness: 2,
        fontSize: 67,
        align: 'center',
      },
      freeSpinsStyle: {
        fill: [0xffffff, 0x58C4D1],
        fontFamily: FONT_BALOO_NAME,
        fontWeight: 900,
        stroke: '#FFFFFF',
        strokeThickness: 2,
        fontSize: 67,
        align: 'center',
      },
      crystalTextStyle: {
        fill: [0xfefefd, 0x9b7853],
        fontFamily: FONT_BALOO_NAME,
        fontWeight: 200,
        stroke: '#fefefd',
        strokeThickness: .5,
        fontSize: 30,
        align: 'center',
        dropShadowColor: 0x000000,
        dropShadowAlpha: .5,
        dropShadowBlur: 2,
        dropShadowDistance: 2,
        dropShadow: true,
        dropShadowAngle: Math.PI / 2,
      },
    };

    this.config = this.nativeConfig;

    this.resourceloader = null;
  }

  overrideStyles({
    titleStyle,
    multiplierStyle,
    freeSpinsStyle,
    crystalTextStyle,
  } = {}) {
    this.config.titleStyle = {
      ...this.config.titleStyle,
      ...titleStyle,
    };
    this.config.multiplierStyle = {
      ...this.config.multiplierStyle,
      ...multiplierStyle,
    };
    this.config.freeSpinsStyle = {
      ...this.config.freeSpinsStyle,
      ...freeSpinsStyle,
    };
    this.config.crystalTextStyle = {
      ...this.config.crystalTextStyle,
      ...crystalTextStyle,
    };
  }

  resetStyles() {
    this.config = this.nativeConfig;
  }

  get resources() {
    if (this._resources) {
      return this._resources;
    }
    return null;
  }

  async loadResource() {
    if (!this.resourceloader) {
      if (!this._resources) {
        this.resourceloader = super.loadResource(bonusPreBonusSharedResources, this.loader);
        const { res } = await this.resourceloader;
        this._resources = res;
      }
      return this.resources;
    } else {
      await this.resourceloader;
      return this.resources;
    }
  }

  __getContentImage({
    name,
    size,
  }) {
    const image = this.createSprite(this.resources[name]);
    image.anchor.set(.5);
    image.scale.set(size / (image.width * .72));
    return image;
  }

  __getTitle({
    name,
  }) {
    const title = new this.PIXI.Text(name, this.config.titleStyle);
    title.anchor.set(.5);
    return title;
  }

  getMultiplier({
    size = 100,
    freeSpacePercent = 16,
  } = {}) {
    const multiPlayerContainer = this.createContainer('multiPlayerContainer');
    const image = this.__getContentImage({ name: bonusPreBonusSharedRssNames.multiplierContent, size });

    const { height } = image;

    const title = this.__getTitle({ name: getTranslation(translationKeys.multiplier) });
    const titleTop = -height / 2 + height * freeSpacePercent / 100 + title.height;
    this.setPosition(title, { y: titleTop });

    const multiplierValue = new this.PIXI.Text('X0', this.config.multiplierStyle);
    multiplierValue.anchor.set(.5);
    this.setPosition(multiplierValue, { y: 15 });

    multiPlayerContainer.addChild(
      image,
      title,
      multiplierValue,
    );

    const onChange = (val) => {
      animationDrawer.blinking(multiplierValue, .2);
      multiplierValue.text = `X${val}`;
    };
    multiPlayerContainer.size = size;
    return [multiPlayerContainer, onChange];
  }

  getFreeSpin({
    size,
    freeSpacePercent = 16,
  } = {}) {
    const freeSpinContainer = this.createContainer('freeSpinContainer');
    const image = this.__getContentImage({ name: bonusPreBonusSharedRssNames.freeSpinContent, size });
    const { height } = image;

    const title = this.__getTitle({ name: getTranslation(translationKeys.freeSpins) });
    const titleTop = -height / 2 + height * freeSpacePercent / 100 + title.height;
    this.setPosition(title, { y: titleTop });

    const value = new this.PIXI.Text(0, this.config.freeSpinsStyle);
    value.anchor.set(.5);
    this.setPosition(value, { y: 15 });

    freeSpinContainer.addChild(
      image,
      title,
      value,
    );

    const onChange = (val, { prev }) => {
      if (prev !== null && val !== prev) {
        animationDrawer.blinking(value, .2);
      }
      value.text = val;
    };
    freeSpinContainer.size = size;
    return [freeSpinContainer, onChange];
  }

  getCrystalsContentX2({
    size = 1,
    // contentPercent = 40,
    crystals = [],
  } = {}) {
    const content = this.createContainer('content');
    const image = this.__getContentImage({ name: bonusPreBonusSharedRssNames.crystalsContentX2, size });

    content.addChild(
      image,
    );
    const contentHeight = size * 17 / 20 * .9;

    const positionX = -size * .25;
    const positions = [
      { x: positionX, y: -contentHeight / 4 },
      { x: positionX, y: contentHeight / 4 },
    ];

    const crystalSize = size * 4 / 15;

    const events = [];
    const containers = crystals.map(({ name, observerName, size = crystalSize }, index) => {
      const container = this.createContainer();
      const text = new this.PIXI.Text('', this.config.crystalTextStyle);
      const crystal = this.createSprite(this.resources[name]);
      events.push(({ [observerName]: val }, { prev }) => {
        text.text = val | 0;
        if (prev && prev[observerName] !== val) {
          animationDrawer.blinking(text, .2);
        }
      });
      text.anchor.set(.5);
      crystal.anchor.set(.5);
      crystal.scale.set(2 * size / crystal.width);

      this.setPosition(container, positions[index]);
      this.setPosition(text, { x: size * 1.4 });
      // this.setPosition(container, { y: y + (index < 2 ? halfHeight : -halfHeight) });
      container.addChild(
        crystal,
        text,
      );
      content.addChild(container);
      return container;
    });

    const onChange = (...args) => {
      events.forEach(method => method(...args));
    };

    return {
      content,
      onChange,
      crystals: containers,
      *[Symbol.iterator]() {
        yield content;
        yield onChange;
        yield containers;
      },
    };
  }

  getCrystalsContentX4({
    size = 300,
    crystals = [],
  } = {}) {
    const content = this.createContainer('CrystalsContentX4');
    const image = this.__getContentImage({ name: bonusPreBonusSharedRssNames.crystalsContentX4, size });

    content.addChild(
      image,
    );

    const contentHeight = size * 13 / 8 * .9;

    const positionX = -size * .25;
    const positions = [
      { x: positionX, y: -contentHeight / 2 },
      { x: positionX, y: -contentHeight / 4 },
      { x: positionX, y: contentHeight / 4 },
      { x: positionX, y: contentHeight / 2 },
    ];

    const crystalSize = size * 4 / 15;

    const events = [];
    const crystalsContainers = crystals.map(({ name, observerName, size = crystalSize }, index) => {
      const container = this.createContainer(name);
      const text = new this.PIXI.Text('', this.config.crystalTextStyle);
      const crystal = this.createSprite(this.resources[name]);
      events.push(({ [observerName]: val }, { prev }) => {
        text.text = val | 0;
        if (prev && prev[observerName] !== val) {
          animationDrawer.blinking(text, .2);
        }
      });
      text.anchor.set(.5);
      crystal.anchor.set(.5);
      crystal.scale.set(2 * size / crystal.width);
      container.addChild(
        crystal,
        text,
      );
      const { y, x } = positions[index];
      const halfHeight = crystalSize / 2;
      this.setPosition(crystal, { x });
      this.setPosition(text, { x: size * .5 });
      this.setPosition(container, { y: y + (index < 2 ? halfHeight : -halfHeight) });
      content.addChild(container);
      return container;
    });

    const onChange = (...args) => {
      events.forEach(method => method(...args));
    };

    return {
      content,
      onChange,
      crystals: crystalsContainers,
      *[Symbol.iterator]() {
        yield content;
        yield onChange;
        yield crystalsContainers;
      },
    };
  }

  getCrystalsContentX3({
    size = 300,
    crystals = [],
  } = {}) {
    const content = this.createContainer('CrystalsContentX4');
    const image = this.__getContentImage({ name: bonusPreBonusSharedRssNames.crystalsContentX3, size });

    content.addChild(
      image,
    );

    const contentHeight = size * 15 / 12;

    const positionX = -size * .25;
    const positions = [
      { x: positionX, y: -contentHeight / 3 },
      { x: positionX, y: 0 },
      { x: positionX, y: contentHeight / 3 },
    ];

    const crystalSize = size * 4 / 15;

    const events = [];
    const crystalsContainers = crystals.map(({ name, observerName, size = crystalSize }, index) => {
      const container = this.createContainer(name);
      const text = new this.PIXI.Text('', this.config.crystalTextStyle);
      const crystal = this.createSprite(this.resources[name]);
      events.push(({ [observerName]: val }, { prev }) => {
        text.text = val | 0;
        if (prev && prev[observerName] !== val) {
          animationDrawer.blinking(text, .2);
        }
      });
      text.anchor.set(.5);
      crystal.anchor.set(.5);
      crystal.scale.set(2 * size / crystal.width);
      container.addChild(
        crystal,
        text,
      );
      const { y, x } = positions[index];
      this.setPosition(text, { x: size * 1.4 });
      this.setPosition(container, { x, y });
      content.addChild(container);
      return container;
    });

    const onChange = (...args) => {
      events.forEach(method => method(...args));
    };

    return {
      content,
      onChange,
      crystals: crystalsContainers,
      *[Symbol.iterator]() {
        yield content;
        yield onChange;
        yield crystalsContainers;
      },
    };
  }

  getCrystalsContentX3_Big({
    size = 300,
    crystals = [],
  } = {}) {
    const content = this.createContainer('CrystalsContentX4');
    const image = this.__getContentImage({ name: bonusPreBonusSharedRssNames.crystalsContentX3_Big, size });

    content.addChild(
      image,
    );

    const contentHeight = size * 15 / 9;

    const positionX = -size * .25;
    const positions = [
      { x: positionX, y: -contentHeight / 3 },
      { x: positionX, y: 0 },
      { x: positionX, y: contentHeight / 3 },
    ];

    const crystalSize = size * 4 / 15;

    const events = [];
    const crystalsContainers = crystals.map(({ name, observerName, size = crystalSize }, index) => {
      const container = this.createContainer(name);
      const text = new this.PIXI.Text('', this.config.crystalTextStyle);
      const crystal = this.createSprite(this.resources[name]);
      events.push(({ [observerName]: val }, { prev }) => {
        text.text = val | 0;
        if (prev && prev[observerName] !== val) {
          animationDrawer.blinking(text, .2);
        }
      });
      text.anchor.set(.5);
      crystal.anchor.set(.5);
      crystal.scale.set(2 * size / crystal.width);
      container.addChild(
        crystal,
        text,
      );
      const { y, x } = positions[index];
      this.setPosition(text, { x: size * 1.4 });
      this.setPosition(container, { x, y });
      content.addChild(container);
      return container;
    });

    const onChange = (...args) => {
      events.forEach(method => method(...args));
    };

    return {
      content,
      onChange,
      crystals: crystalsContainers,
      *[Symbol.iterator]() {
        yield content;
        yield onChange;
        yield crystalsContainers;
      },
    };
  }

  getPreBonusLightEffect({ size, name = bonusPreBonusSharedRssNames.pre_bonus_free_spin_light_effect } = {}) {
    const topPlantsTexture = this.createSpine(this.resources[name]);
    topPlantsTexture.width = size;
    topPlantsTexture.height = size;
    topPlantsTexture.visible = false;

    const start = () => {
      topPlantsTexture.visible = true;
      topPlantsTexture.state.setAnimation(0, 'animation', false);
    };

    return {
      content: topPlantsTexture,
      start,
      *[Symbol.iterator]() {
        yield topPlantsTexture;
        yield start;
      },
    };
  }

  getButtonContainer() {
    const image = this.createSprite(this.resources[bonusPreBonusSharedRssNames.button_container]);
    image.anchor.set(.5);
    return image;
  }

  getTextContainer() {
    const container = this.createContainer();
    const textImage = this.createSprite(this.resources[bonusPreBonusSharedRssNames.text_container]);
    textImage.anchor.set(.5);

    container.addChild(
      textImage,
    );

    return container;
  }

}

export const bonusPreBonusSharedDrawer = new BonusPreBonusSharedDrawer();
